import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { jwtDecode } from 'jwt-decode';
import { Observable, throwError, tap, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SignOutRequest } from '../models/auth.request';
import { RefreshTokenResponse, UserInfo, DecodedToken } from '../models/auth.response';
import { STORAGE_KEYS } from '../constants/storage-keys';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly cookieService = inject(CookieService);

  /** Cached decoded token — invalidated on setTokens/clearAuthData */
  private cachedUserInfo: UserInfo | null = null;
  private cachedTokenString: string | null = null;
  private migrating = false;
  private readonly _userInfo = signal<UserInfo | null>(this.getUserInfo());
  readonly userInfo = this._userInfo.asReadonly();
  private readonly _isAuthenticated = signal<boolean>(this.isAuthenticated());
  readonly authStatus = this._isAuthenticated.asReadonly();

  private isLocalhost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  /** Check if running on the production domain family (rcscbs.com / brijjworks.com) */
  private isProductionDomain(): boolean {
    const host = window.location.hostname;
    return host.endsWith(environment.domainName);
  }

  // ── Token Management ─────────────────────────────────────────────────────

  setTokens(accessToken: string, refreshToken: string, sessionId: string): void {
    if (!accessToken || !refreshToken || !sessionId) {
      if (!environment.production) console.error('setTokens: all token values must be non-empty');
      return;
    }

    this.invalidateCache();

    const opts = this.getCookieOptions();
    this.cookieService.set('donor_accessToken', accessToken, opts);
    this.cookieService.set('donor_refreshToken', refreshToken, opts);
    this.cookieService.set('donor_sessionId', sessionId, opts);
    this.updateSignals();
  }

  setAccessToken(accessToken: string): void {
    if (!accessToken) {
      if (!environment.production) console.error('setAccessToken: token must be non-empty');
      return;
    }

    this.invalidateCache();
    this.cookieService.set('donor_accessToken', accessToken, this.getCookieOptions());
    this.updateSignals();
  }

  /**
   * Cookie options with security attributes.
   * Secure: only sent over HTTPS (skipped on localhost for dev).
   * SameSite: Lax prevents CSRF while allowing navigation-initiated requests.
   * Domain-scoped cookies only on production domains (*.brijjworks.com / *.rcscbs.com).
   * On non-production hosts (Vercel, Netlify, etc.), cookies are host-only (no domain attribute).
   */
  private getCookieOptions(): { path: string; domain?: string; secure: boolean; sameSite: 'Lax' | 'Strict' | 'None' } {
    if (this.isLocalhost()) {
      return { path: '/', secure: false, sameSite: 'Lax' };
    }
    if (this.isProductionDomain()) {
      return { path: '/', domain: `.${environment.domainName}`, secure: true, sameSite: 'Lax' };
    }
    // Non-production HTTPS hosts (Vercel, Netlify, preview deploys)
    return { path: '/', secure: true, sameSite: 'Lax' };
  }

  private migrateCookies(): void {
    if (this.migrating) return;
    this.migrating = true;
    try {
      // 1. Check if we already have a valid session in donor_accessToken
      const existingToken = this.cookieService.get('donor_accessToken');
      if (existingToken) {
        const decoded = this.decodeToken(existingToken);
        if (decoded?.exp && Date.now() < decoded.exp * 1000 - 10000) {
          // If existing namespaced token is valid, do not overwrite it
          return;
        }
      }

      // 2. Otherwise, check for generic SSO cookies
      const accessToken = this.cookieService.get('accessToken');
      const refreshToken = this.cookieService.get('refreshToken');
      const sessionId = this.cookieService.get('sessionId');

      if (accessToken) {
        // Only migrate if the token belongs to an external (donor) user
        const decoded = this.decodeToken(accessToken);
        const userInfo = decoded ? this.toUserInfo(decoded) : null;
        if (userInfo?.userOrigin === 'External') {
          const opts = this.getCookieOptions();
          this.cookieService.set('donor_accessToken', accessToken, opts);
          if (refreshToken) {
            this.cookieService.set('donor_refreshToken', refreshToken, opts);
          }
          if (sessionId) {
            this.cookieService.set('donor_sessionId', sessionId, opts);
          }
          this.updateSignals();
        }
      }
    } finally {
      this.migrating = false;
    }
  }

  getAccessToken(): string | null {
    this.migrateCookies();
    return this.cookieService.get('donor_accessToken') || null;
  }

  getRefreshToken(): string | null {
    this.migrateCookies();
    return this.cookieService.get('donor_refreshToken') || null;
  }

  getSessionId(): string | null {
    this.migrateCookies();
    return this.cookieService.get('donor_sessionId') || null;
  }

  // ── Token Decoding ───────────────────────────────────────────────────────

  decodeToken(token: string): DecodedToken | null {
    try {
      return jwtDecode<DecodedToken>(token);
    } catch (error) {
      if (!environment.production) console.error('Invalid token', error);
      return null;
    }
  }

  /**
   * Get user info from current JWT token.
   * Caches the decoded result to avoid repeated base64 decoding.
   */
  getUserInfo(): UserInfo | null {
    const token = this.getAccessToken();
    if (!token) {
      this.cachedUserInfo = null;
      this.cachedTokenString = null;
      return null;
    }

    // Return cached if token hasn't changed
    if (token === this.cachedTokenString && this.cachedUserInfo) {
      return this.cachedUserInfo;
    }

    this.cachedTokenString = token;
    const decoded = this.decodeToken(token);
    this.cachedUserInfo = decoded ? this.toUserInfo(decoded) : null;
    return this.cachedUserInfo;
  }

  // ── Authentication Check ─────────────────────────────────────────────────

  /**
   * Check if user has a valid, non-expired JWT.
   * Includes a 10-second buffer before expiry to allow proactive refresh.
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    const decoded = this.decodeToken(token);
    if (!decoded?.exp) return false;

    const expirationTime = decoded.exp * 1000;
    return Date.now() < expirationTime - 10000;
  }

  // ── Session Cleanup ──────────────────────────────────────────────────────

  /**
   * Clear all authentication data — cookies, localStorage, sessionStorage.
   * Only removes auth-related keys, not unrelated app state.
   */
  clearAuthData(): void {
    this.invalidateCache();

    // Clear auth-related storage keys only
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
    sessionStorage.removeItem(STORAGE_KEYS.DONOR_RETURN_URL);

    const keysToDelete = ['donor_accessToken', 'donor_refreshToken', 'donor_sessionId', 'donor_logoutEvent'];

    // Delete auth cookies
    if (this.isLocalhost()) {
      keysToDelete.forEach(key => this.cookieService.delete(key, '/'));
    } else if (this.isProductionDomain()) {
      const domain = `.${environment.domainName}`;
      keysToDelete.forEach(key => {
        this.cookieService.delete(key, '/', domain);
        this.cookieService.delete(key, '/', environment.domainName);
      });
    } else {
      // Non-production hosts (Vercel, Netlify, etc.) — host-only cookies
      keysToDelete.forEach(key => this.cookieService.delete(key, '/'));
    }

    this.updateSignals();
  }

  // ── Company App Integration (production) ─────────────────────────────────

  refreshAccessToken(refreshToken: string): Observable<RefreshTokenResponse> {
    const url = `${environment.companyApiUrl}/ExternalUsers/RefreshToken`;
    return this.http.post<RefreshTokenResponse>(url, { refreshToken });
  }

  /**
   * Refreshes the access token using the stored refresh token.
   * Updates the `accessToken` cookie and cache on success.
   * Returns an Observable of the new accessToken string.
   */
  refreshAndStore(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.refreshAccessToken(refreshToken).pipe(
      tap(response => {
        this.setAccessToken(response.accessToken);
      }),
      map(response => response.accessToken)
    );
  }

  logout(userDetails: SignOutRequest): Observable<unknown> {
    const url = `${environment.companyApiUrl}/ExternalUsers/SignOut`;
    return this.http.post(url, userDetails);
  }

  /**
   * Full logout — calls Company API to invalidate server session,
   * clears local data, then redirects. For production use.
   */
  performLogout(): void {
    const sessionId = this.getSessionId();
    const userId = this.getUserInfo()?.userId;

    this.clearAuthData();

    if (sessionId) {
      this.logout({ sessionId, userId }).subscribe({
        error: err => {
          if (!environment.production) console.error('Logout API error:', err);
        }
      });
    }
  }

  // ── Cross-app logout broadcast ────────────────────────────────────────────

  /**
   * Set a shared `logoutEvent` cookie visible to all subdomains.
   * Any other tab or app polling for this cookie will sign itself out.
   */
  broadcastLogout(): void {
    const expires = new Date();
    expires.setMinutes(expires.getMinutes() + 10);
    if (this.isLocalhost()) {
      // On localhost all ports share the same cookie jar — no domain or Secure flag needed
      this.cookieService.set('donor_logoutEvent', '1', expires, '/', undefined, false, 'Lax');
    } else {
      this.cookieService.set('donor_logoutEvent', '1', expires, '/', `.${environment.domainName}`, true, 'Lax');
    }
  }

  /**
   * Remove the `donor_logoutEvent` broadcast cookie.
   * Call after detecting the cookie (to prevent re-triggering) or after login.
   */
  clearLogoutBroadcast(): void {
    const keys = ['donor_logoutEvent', 'logoutEvent'];
    keys.forEach(key => {
      this.cookieService.delete(key, '/');
      this.cookieService.delete(key, '/', '.localhost');
      if (this.isProductionDomain()) {
        const domain = `.${environment.domainName}`;
        this.cookieService.delete(key, '/', domain);
        this.cookieService.delete(key, '/', environment.domainName);
      }
    });
  }

  /** Returns true if the `donor_logoutEvent` broadcast cookie is present. */
  hasLogoutBroadcast(): boolean {
    return document.cookie.split(';').some(c => c.trim().startsWith('donor_logoutEvent='));
  }

  /**
   * Safely convert a decoded token to UserInfo.
   * Validates that required fields exist rather than using an unsafe cast.
   */
  private toUserInfo(decoded: DecodedToken): UserInfo | null {
    if (!decoded.exp || !decoded.iat || !decoded.nbf) return null;
    const rawRole = decoded['custom:role'];
    const roles = Array.isArray(rawRole) ? rawRole : rawRole ? [rawRole] : [];
    return {
      userId: decoded['custom:userid'] ?? decoded.sub ?? '',
      companyId: decoded['custom:companyid'] ?? '',
      email: decoded['custom:email'] ?? (decoded as Record<string, string | undefined>)['email'] ?? decoded.sub ?? '',
      fullName: decoded['custom:fullname'] ?? '',
      roles,
      phoneNumber: decoded['custom:phonenumber'],
      mustChangePassword: decoded['custom:mustchangepasswordonlogin'],
      userType: decoded['custom:usertype'],
      userOrigin: decoded['custom:userorigin'],
      timezoneCode: decoded['custom:timezonecode'],
      exp: decoded.exp,
      iat: decoded.iat,
      nbf: decoded.nbf,
      iss: decoded.iss ?? ''
    };
  }

  private invalidateCache(): void {
    this.cachedUserInfo = null;
    this.cachedTokenString = null;
  }

  private updateSignals(): void {
    this._userInfo.set(this.getUserInfo());
    this._isAuthenticated.set(this.isAuthenticated());
  }
}
